import { Box, Paper, Typography } from "@mui/material";
import { useId, useMemo } from "react";
import { WinLossStackedColumnChart } from "../../../../components/charts/WinLossStackedColumnChart/WinLossStackedColumnChart";
import { WinLossStackedColumnChartData } from "../../../../components/charts/WinLossStackedColumnChart/types";
import { pluralize } from "../../../../utils/text";
import { ICPChartSkeleton } from "./ICPChartSkeleton";

interface WinLossDataItem {
  countWon: number;
  countLost: number;
  totalCount: number;
  isSignificant: boolean | number;
}

interface GenericICPWinLossChartProps<T extends WinLossDataItem> {
  /** Win/loss distribution data from API */
  data: T[];
  /** Function to extract category name from data item */
  getCategoryName: (item: T) => string;
  /** Height of the chart container */
  height?: string;
  /** Optional total count to display (overrides calculated total from data) */
  totalCount?: number;
  /** Optional custom sorting function for data items */
  sortData?: (a: T, b: T) => number;
  /** Optional custom filter function for data items */
  filterData?: (item: T) => boolean;
  /** Segment name to include in the chart title (e.g., "Industry", "Geography") */
  segment?: string;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Generic component that displays win/loss distribution chart with stacked columns
 * Each column shows win and loss segments that total 100%
 */
export const GenericICPWinLossChart = <T extends WinLossDataItem>({
  data,
  getCategoryName,
  height = "350px",
  totalCount: overrideTotalCount,
  sortData,
  filterData,
  segment,
  isLoading = false,
}: GenericICPWinLossChartProps<T>) => {
  const chartId = useId();

  const chartData: WinLossStackedColumnChartData[] = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Apply custom filter if provided, otherwise default filter, then sort and limit to top 10
    const filteredData = data
      .filter(filterData || ((item) => item.totalCount > 0))
      .sort(sortData || ((a, b) => b.totalCount - a.totalCount))
      .slice(0, 10);

    return filteredData.map((item) => {
      const winPercentage =
        item.totalCount > 0
          ? Math.round((item.countWon / item.totalCount) * 100)
          : 0;
      const lossPercentage = 100 - winPercentage;

      return {
        category: getCategoryName(item),
        winCount: item.countWon,
        lossCount: item.countLost,
        totalCount: item.totalCount,
        winPercentage: `${winPercentage}%`,
        lossPercentage: `${lossPercentage}%`,
        winCountText: pluralize("win", item.countWon),
        lossCountText: pluralize("loss", item.countLost),
      };
    });
  }, [data, getCategoryName, sortData, filterData]);

  const totalCount = useMemo(() => {
    // Use override total count if provided, otherwise calculate from data
    return (
      overrideTotalCount ?? data.reduce((sum, item) => sum + item.totalCount, 0)
    );
  }, [data, overrideTotalCount]);

  if (isLoading) {
    return <ICPChartSkeleton height={height} />;
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          height,
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          gap: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#3a3a3a",
            }}
          >
            {segment ? `${segment} Win/Loss Analysis` : "Win/Loss Analysis"}
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              color: "text.secondary",
              fontWeight: "normal",
              whiteSpace: "nowrap",
            }}
          >
            ({totalCount.toLocaleString()} {pluralize("company", totalCount)})
          </Typography>
        </Box>
        {chartData.length > 0 ? (
          <WinLossStackedColumnChart
            id={chartId}
            data={chartData}
            height="100%"
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No win/loss data available</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
